import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Shield,
  Users,
  Brain,
  Trophy,
  ArrowRight,
  CheckCircle,
  Briefcase,
  GraduationCap,
  FileText,
  Target,
  BarChart,
  UserCheck,
  ClipboardList,
  Award,
  Zap,
  TrendingUp,
  Video,
  Search,
} from "lucide-react";
import BookDemoModal from "../components/BookDemoModal";

const LandingPage: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUserType, setSelectedUserType] = useState<"jobseeker" | "hr">("jobseeker");

  const jobSeekerServices = [
    {
      icon: Brain,
      title: "AI-Powered Skill Assessments",
      description: "Take intelligent tests that adapt to your skill level and provide instant feedback on your performance."
    },
    {
      icon: FileText,
      title: "Resume Parsing & Optimization",
      description: "Upload your resume and get AI-powered insights to optimize it for better job matches."
    },
    {
      icon: Award,
      title: "Skills Verification & Badges",
      description: "Earn verified badges after completing assessments to showcase your skills to potential employers."
    },
    {
      icon: Video,
      title: "AI Interview Practice",
      description: "Practice with AI-driven interviews that simulate real hiring scenarios and improve your interview skills."
    },
    {
      icon: GraduationCap,
      title: "Personalized Learning Path",
      description: "Get customized course recommendations based on your test results and career goals."
    },
    {
      icon: TrendingUp,
      title: "Application Tracking",
      description: "Track all your job applications and test submissions in one centralized dashboard."
    },
    {
      icon: Target,
      title: "Skill Gap Analysis",
      description: "Identify areas for improvement with detailed analytics on your strengths and weaknesses."
    },
    {
      icon: Zap,
      title: "Instant Results & Feedback",
      description: "Receive immediate test results with comprehensive feedback to help you improve continuously."
    }
  ];

  const hrServices = [
    {
      icon: Briefcase,
      title: "AI Test Generation",
      description: "Upload job descriptions and automatically generate customized technical tests tailored to your requirements."
    },
    {
      icon: Search,
      title: "Automated Candidate Screening",
      description: "Screen hundreds of candidates efficiently with AI-powered evaluation and ranking systems."
    },
    {
      icon: Shield,
      title: "Advanced Proctoring",
      description: "Monitor tests with camera tracking, tab switching detection, and comprehensive violation reporting."
    },
    {
      icon: UserCheck,
      title: "Candidate Management",
      description: "Manage your candidate pipeline with easy shortlist, reject, and save features for better organization."
    },
    {
      icon: BarChart,
      title: "Detailed Analytics Dashboard",
      description: "Access comprehensive analytics on candidate performance, test completion rates, and hiring metrics."
    },
    {
      icon: ClipboardList,
      title: "Job Posting Management",
      description: "Create and manage multiple job postings, each with custom tests and evaluation criteria."
    },
    {
      icon: Users,
      title: "Bulk Candidate Evaluation",
      description: "Evaluate large batches of candidates simultaneously with automated scoring and comparison tools."
    },
    {
      icon: Trophy,
      title: "Skills-Based Matching",
      description: "Match candidates to positions using AI-powered skill analysis and compatibility scoring."
    }
  ];

  const features = [
    {
      icon: Brain,
      title: "AI-Powered Testing",
      description:
        "Generate adaptive tests automatically from job descriptions using advanced AI",
    },
    {
      icon: Users,
      title: "Complete Hiring Solution",
      description:
        "End-to-end platform for HR teams, recruiters, and job seekers",
    },
    {
      icon: Trophy,
      title: "Skill Verification",
      description:
        "Verified badges and certifications to showcase candidate abilities",
    },
    {
      icon: Shield,
      title: "Enterprise Security",
      description:
        "Bank-level security and privacy protection for all your data",
    },
  ];

  const testimonials = [
    {
      name: "Sarah Johnson",
      role: "HR Director at TechCorp",
      quote:
        "VetriaAI reduced our screening time by 80% while improving candidate quality.",
      avatar:
        "https://images.pexels.com/photos/774909/pexels-photo-774909.jpeg?auto=compress&cs=tinysrgb&w=150",
    },
    {
      name: "Michael Chen",
      role: "Software Engineer",
      quote:
        "The AI interview process was surprisingly engaging and fair. Loved the experience!",
      avatar:
        "https://images.pexels.com/photos/220453/pexels-photo-220453.jpeg?auto=compress&cs=tinysrgb&w=150",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
      {/* Navigation */}
      <nav className="glass-card border-0 border-b border-white/20 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <img
              src="/vetriaAI.png"
              alt="VetriaAI Logo"
              className="w-10 h-10"
              width="40"
              height="40"
              loading="eager"
            />
            <span className="text-2xl font-bold text-gray-900">VetriaAI</span>
          </div>

          <div className="flex items-center space-x-4">
            <Link
              to="/signup"
              className="btn-secondary"
            >
              Sign Up
            </Link>
            <Link
              to="/login"
              className="btn-primary"
            >
              Sign In
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative py-32 px-6 overflow-hidden">
        {/* Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 opacity-10"></div>

        <div className="max-w-7xl mx-auto text-center relative z-10">
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-6 leading-tight">
            Revolutionize Your Hiring with
            <span className="block mt-2 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 bg-clip-text text-transparent">
              AI-Powered Intelligence
            </span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 mb-12 max-w-3xl mx-auto leading-relaxed">
            Transform your recruitment process with intelligent testing,
            automated screening, and data-driven insights. Perfect for HR teams,
            recruiters, and job seekers.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link
              to="/signup"
              className="btn-primary text-lg px-10 py-5"
            >
              Get Started <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            <button
              onClick={() => setIsModalOpen(true)}
              className="btn-secondary text-lg px-10 py-5"
            >
              Book Your Demo
            </button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Everything You Need to Hire Smarter
            </h2>
            <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto">
              Our comprehensive platform combines AI technology with human
              expertise to deliver exceptional hiring outcomes.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className="glass-card p-8 hover:shadow-card-hover hover:-translate-y-2 transition-all duration-300"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed text-lg">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Key Goals Section */}
      <section className="py-24 px-6 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Key Goals (Future-Facing Metrics)
            </h2>
            <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto">
              Our ambitious roadmap to transform hiring across industries
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="glass-card p-8 text-center hover:shadow-card-hover hover:-translate-y-2 transition-all duration-300">
              <div className="text-5xl mb-6">🎯</div>
              <div className="text-4xl font-bold bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 bg-clip-text text-transparent mb-4">
                10,000+
              </div>
              <div className="text-gray-600 font-medium text-lg">
                Candidates screened within the first year
              </div>
            </div>
            <div className="glass-card p-8 text-center hover:shadow-card-hover hover:-translate-y-2 transition-all duration-300">
              <div className="text-5xl mb-6">🎯</div>
              <div className="text-4xl font-bold bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 bg-clip-text text-transparent mb-4">
                500+
              </div>
              <div className="text-gray-600 font-medium text-lg">
                Companies supported by 2026
              </div>
            </div>
            <div className="glass-card p-8 text-center hover:shadow-card-hover hover:-translate-y-2 transition-all duration-300">
              <div className="text-5xl mb-6">🎯</div>
              <div className="text-4xl font-bold bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 bg-clip-text text-transparent mb-4">
                80%
              </div>
              <div className="text-gray-600 font-medium text-lg">
                Hiring time reduction across pilot users
              </div>
            </div>
            <div className="glass-card p-8 text-center hover:shadow-card-hover hover:-translate-y-2 transition-all duration-300">
              <div className="text-5xl mb-6">🎯</div>
              <div className="text-4xl font-bold bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 bg-clip-text text-transparent mb-4">
                95%+
              </div>
              <div className="text-gray-600 font-medium text-lg">
                Accuracy in skill-based shortlisting
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Our Services
            </h2>
            <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto mb-10">
              Discover what we offer for both job seekers and HR professionals
            </p>

            {/* Toggle Button */}
            <div className="inline-flex items-center glass-card p-2 shadow-lg">
              <button
                onClick={() => setSelectedUserType("hr")}
                className={`px-8 py-4 rounded-xl font-semibold transition-all duration-300 ${
                  selectedUserType === "hr"
                    ? "bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white shadow-lg"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                For HRs / Founders
              </button>
              <button
                onClick={() => setSelectedUserType("jobseeker")}
                className={`px-8 py-4 rounded-xl font-semibold transition-all duration-300 ${
                  selectedUserType === "jobseeker"
                    ? "bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white shadow-lg"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                For Job Seekers
              </button>
            </div>
          </div>

          {/* Services Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {(selectedUserType === "jobseeker" ? jobSeekerServices : hrServices).map((service, index) => {
              const Icon = service.icon;
              return (
                <div
                  key={index}
                  className="glass-card p-8 hover:shadow-card-hover hover:-translate-y-2 transition-all duration-300"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {service.description}
                  </p>
                </div>
              );
            })}
          </div>

          {/* CTA */}
          <div className="text-center mt-16">
            <Link
              to="/login"
              className="btn-primary text-lg px-10 py-5"
            >
              Get Started <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Featured By Section */}
      <section className="py-24 px-6 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Featured By
            </h2>
            <p className="text-xl text-gray-600">
              Recognized by leading platforms and ecosystems
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            {/* FindMyAI */}
            <a
              href="https://www.linkedin.com/company/fyndmyai/posts/?feedView=all"
              target="_blank"
              rel="noopener noreferrer"
              className="glass-card p-8 hover:shadow-card-hover hover:-translate-y-2 transition-all duration-300 flex flex-col items-center justify-center text-center"
            >
              <div className="w-28 h-28 mb-6 flex items-center justify-center">
                <img
                  src="/findMyAi.jpg"
                  alt="FindMyAI Logo"
                  className="w-full h-full object-contain rounded-xl"
                  width="196"
                  height="196"
                  loading="lazy"
                />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                FindMyAI
              </h3>
              <p className="text-gray-600">
                Featured on FindMyAI Platform
              </p>
            </a>

            {/* Telangana Startup */}
            <a
              href="https://www.tg10x.com/companies/vetriaai"
              target="_blank"
              rel="noopener noreferrer"
              className="glass-card p-8 hover:shadow-card-hover hover:-translate-y-2 transition-all duration-300 flex flex-col items-center justify-center text-center"
            >
              <div className="w-28 h-28 mb-6 flex items-center justify-center">
                <img
                  src="/Telangana_startup.jpg"
                  alt="Telangana Startup Logo"
                  className="w-full h-full object-contain rounded-xl"
                  width="196"
                  height="196"
                  loading="lazy"
                />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Telangana Startup Ecosystem
              </h3>
              <p className="text-gray-600">
                Part of Telangana's Innovation Hub
              </p>
            </a>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="relative py-24 px-6 overflow-hidden">
        {/* Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500"></div>

        <div className="max-w-4xl mx-auto text-center relative z-10 text-white">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to transform your hiring?
          </h2>
          <p className="text-xl md:text-2xl mb-10 opacity-95">
            Join early adopters using VetriaAI to hire smarter.
          </p>
          <button
            onClick={() => setIsModalOpen(true)}
            className="bg-white text-brand-purple-600 px-10 py-5 rounded-xl hover:shadow-2xl transition-all duration-300 font-bold text-lg inline-flex items-center hover:-translate-y-1 gap-2"
          >
            Book Your Demo <ArrowRight className="w-5 h-5" strokeWidth={2.5} />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-5 gap-12">
            <div>
              <div className="flex items-center space-x-3 mb-6">
                <img
                  src="/vetriaAI.png"
                  alt="VetriaAI Logo"
                  className="w-10 h-10"
                  width="40"
                  height="40"
                  loading="lazy"
                />
                <span className="text-2xl font-bold">VetriaAI</span>
              </div>
              <p className="text-gray-400 text-lg">
                Revolutionizing recruitment with AI-powered intelligence.
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-6 text-xl text-white">
                Product
              </h3>
              <ul className="space-y-3">
                <li>
                  <Link to="/features" className="text-white hover:text-gray-300 transition-colors text-lg">
                    Features
                  </Link>
                </li>
                <li>
                  <Link to="/security" className="text-white hover:text-gray-300 transition-colors text-lg">
                    Security
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-6 text-xl text-white">
                Company
              </h3>
              <ul className="space-y-3">
                <li>
                  <Link to="/about" className="text-white hover:text-gray-300 transition-colors text-lg">
                    About
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="text-white hover:text-gray-300 transition-colors text-lg">
                    Contact
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-6 text-xl text-white">
                Support
              </h3>
              <ul className="space-y-3">
                <li>
                  <Link to="/help-center" className="text-white hover:text-gray-300 transition-colors text-lg">
                    Help Center
                  </Link>
                </li>
                <li>
                  <a href="#" className="text-white hover:text-gray-300 transition-colors text-lg">
                    Documentation
                  </a>
                </li>
                <li>
                  <Link to="/status" className="text-white hover:text-gray-300 transition-colors text-lg">
                    Status
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-6 text-xl text-white">
                Legal
              </h3>
              <ul className="space-y-3">
                <li>
                  <Link to="/cancellations-refunds" className="text-white hover:text-gray-300 transition-colors text-lg">
                    Cancellations &amp; Refunds
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="text-white hover:text-gray-300 transition-colors text-lg">
                    Terms &amp; Conditions
                  </Link>
                </li>
                <li>
                  <Link to="/shipping" className="text-white hover:text-gray-300 transition-colors text-lg">
                    Shipping (Digital)
                  </Link>
                </li>
                <li>
                  <Link to="/privacy" className="text-white hover:text-gray-300 transition-colors text-lg">
                    Privacy Policy
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-12 pt-10 text-center text-gray-300 text-lg">
            <p>&copy; 2025 VetriaAI. All rights and trademarks are reserved by VetriaAI.</p>
          </div>
        </div>
      </footer>

      <BookDemoModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
};

export default LandingPage;
